import './index.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { useRevealOnScroll } from './components/ui/Reveal';

import AdminLayout from './admin/AdminLayout';
import ProtectedRoute from './admin/ProtectedRoute';

// The homepage is loaded eagerly so the first paint is instant; everything else
// is code-split per route.
import HomePage from './pages/home/HomePage';

const page = <T extends { default: React.ComponentType<any> }>(loader: () => Promise<T>) => lazy(loader);

/* ---------------- Public pages ---------------- */
const ColorVisualizerPage = page(() => import('./pages/ColorVisualizer'));
const ColorGuide = page(() => import('./pages/ColorGuide'));
const SampleRequest = page(() => import('./pages/SampleRequest'));
const ProductsPage = page(() => import('./pages/products/ProductsPage'));
const ProductDetail = page(() => import('./pages/products/ProductDetail'));
const ServicesPage = page(() => import('./pages/services/ServicesPage'));
const AboutPage = page(() => import('./pages/about/AboutPage'));
const ContactPage = page(() => import('./pages/contact/ContactPage'));
const BlogPage = page(() => import('./pages/blog/BlogPage'));
const BlogDetail = page(() => import('./pages/blog/BlogDetail'));
const ColorSchemeTemplatePage = page(() => import('./pages/products/ColorSchemeTemplatePage'));
const GuideList = page(() => import('./pages/guides/GuideList'));
const GuideDetail = page(() => import('./pages/guides/GuideDetail'));
const PrivacyPolicyPage = page(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfUsePage = page(() => import('./pages/legal/TermsOfUsePage'));
const SitemapPage = page(() => import('./pages/legal/SitemapPage'));
const TexturesPage = page(() => import('./pages/products/TexturesPage'));
const TextureDetail = page(() => import('./pages/products/TextureDetail'));
const MarmovellaPage = page(() => import('./pages/products/MarmovellaPage'));
const ColourRoomsPage = page(() => import('./pages/colours/ColourRoomsPage'));
const RoomColoursPage = page(() => import('./pages/colours/RoomColoursPage'));
const ColourCombinationsPage = page(() => import('./pages/colours/ColourCombinationsPage'));
const ColourCatalogue = page(() => import('./pages/colours/ColourCatalogue'));
const WaterproofingPage = page(() => import('./pages/services/WaterproofingPage'));
const BookPainterPage = page(() => import('./pages/services/BookPainterPage'));
const StoreLocatorPage = page(() => import('./pages/services/StoreLocatorPage'));
const TrackOrderPage = page(() => import('./pages/services/TrackOrderPage'));
const LoginPage = page(() => import('./pages/services/LoginPage'));
const CartPage = page(() => import('./pages/services/CartPage'));
const SafePaintingPage = page(() => import('./pages/services/SafePaintingPage'));
const ColourConsultancyPage = page(() => import('./pages/services/ColourConsultancyPage'));
const DealerLocatorPage = page(() => import('./pages/services/DealerLocatorPage'));
const PaintCalculatorPage = page(() => import('./pages/services/PaintCalculatorPage'));
const TDS = page(() => import('./pages/TDS'));
const CalculateNow = page(() => import('./pages/products/CalculateNow'));
const InteriorColorOptions = page(() => import('./pages/products/InteriorColorOptions'));
const OurStory = page(() => import('./pages/company/OurStory'));
const VideosPage = page(() => import('./pages/company/VideosPage'));
const TestimonialsPage = page(() => import('./pages/company/TestimonialsPage'));
const URLQRGenerator = page(() => import('./pages/QR'));
const CatalogFallback = page(() => import('./pages/CatalogFallback'));

/* ---------------- Category pages ---------------- */
const InteriorPaintsPage = page(() => import('./pages/products/InteriorPaintsPage'));
const ExteriorPaintsPage = page(() => import('./pages/products/ExteriorPaintsPage'));
const UnderCoat = page(() => import('./pages/products/UnderCoat'));
const MetalPaints = page(() => import('./pages/products/MetalPaints'));
const WoodPaints = page(() => import('./pages/products/WoodPaints'));
const WaterProfingAndConstructionChemicals = page(() => import('./pages/products/WaterProfingAndConstructionChemicals'));
const OtherSurface = page(() => import('./pages/products/OtherSurface'));

/* ---------------- Product pages (thin wrappers around ProductPage) ---------------- */
const FineSquareInteriorEmulsion = page(() => import('./pages/products/FineSquareInteriorEmulsion'));
const FineSquareExteriorEmulsion = page(() => import('./pages/products/FineSquareExteriorEmulsion'));
const PremiumEmlusion = page(() => import('./pages/products/PremiumEmlusion'));
const FineSquare = page(() => import('./pages/products/Finesquare'));
const SilkShine = page(() => import('./pages/products/SilkShine'));
const UltraWrp = page(() => import('./pages/products/UltraWrp'));
const SelectMattFinish = page(() => import('./pages/products/SelectMattFinish'));
const SelectSheen = page(() => import('./pages/products/SelectSheen'));
const ACMEExteriorEmulsion = page(() => import('./pages/products/ACMEExteriorEmulsion'));
const PrimerBrown = page(() => import('./pages/products/PrimerBrown'));
const FS_HISHEEN = page(() => import('./pages/products/FS_HISHEEN'));
const ACMEWrp = page(() => import('./pages/products/ACMEWrp'));
const SilkShine_SuperGloss = page(() => import('./pages/products/SilkShine_SuperGloss'));
const TitaniumOne = page(() => import('./pages/products/TitaniumOne'));
const BDSeal = page(() => import('./pages/products/BDSeal'));
const UltraWRPExteriorEmulsion = page(() => import('./pages/products/UltraWRPExteriorEmulsion'));
const SS_PearlGloss = page(() => import('./pages/products/SS_PearlGloss'));
const CeilingWhite = page(() => import('./pages/products/CeilingWhite'));
const EasyWashLuxuryEmulsion = page(() => import('./pages/products/EasyWashLuxuryEmulsion'));
const SilkShineSpecialEffectpaints = page(() => import('./pages/products/SilkShineSpecialEffectpaints'));
const AcmeWaterThinnablePrimer = page(() => import('./pages/products/AcmeWaterThinnablePrimer'));
const VertexWaterThinnablePrimer = page(() => import('./pages/products/VertexWaterThinnablePrimer'));
const BaseCoat = page(() => import('./pages/products/BaseCoat'));
const UltraWallSealer = page(() => import('./pages/products/UltraWallSealer'));
const FineSquareInteriorPrimer = page(() => import('./pages/products/FineSquareInteriorPrimer'));
const MGRedOxide = page(() => import('./pages/products/MGRedOxide'));
const ZinchromatePrimer = page(() => import('./pages/products/ZinchromatePrimer'));
const ZinPhosphatePrimerWhite = page(() => import('./pages/products/ZinPhosphatePrimerWhite'));
const PrimerSurfaceBrown = page(() => import('./pages/products/PrimerSurfaceBrown'));
const PrimerSurfaceWhite = page(() => import('./pages/products/PrimerSurfaceWhite'));
const DampProofWallPutty = page(() => import('./pages/products/DampProofWallPutty'));
const PremiumWallPutty = page(() => import('./pages/products/PremiumWallPutty'));
const TitaniumWallPutty = page(() => import('./pages/products/TitaniumWallPutty'));
const Aluminum = page(() => import('./pages/products/Aluminum'));
const PUAluminumPaint = page(() => import('./pages/products/PUAluminumPaint'));
const PUMetallicGoldenPaint = page(() => import('./pages/products/PUMetallicGoldenPaint'));
const SilverAshEmerlandGreen = page(() => import('./pages/products/SilverAshEmerlandGreen'));
const MetalClearGloss = page(() => import('./pages/products/MetalClearGloss'));
const MetalClearMatt = page(() => import('./pages/products/MetalClearMatt'));
const PremiumGlossEnamel = page(() => import('./pages/products/PremiumGlossEnamel'));
const PUHighPerformanceEnamel = page(() => import('./pages/products/PUHighPerformanceEnamel'));
const PremiumGlossEnamelWoodPaints = page(() => import('./pages/products/PremiumGlossEnamelWoodPaints'));
const PUHighPerformanceEnamelWoodPaints = page(() => import('./pages/products/PUHighPerformanceEnamelWoodPaints'));
const WoodGuard = page(() => import('./pages/products/WoodGuard'));
const TrueWoodPUClear = page(() => import('./pages/products/TrueWoodPUClear'));
const ClearVarnish = page(() => import('./pages/products/ClearVarnish'));
const VertexCoolDampProof = page(() => import('./pages/products/VertexCoolDampProof'));
const SmartPolymer = page(() => import('./pages/products/SmartPolymer'));
const WaterproofingTexture = page(() => import('./pages/products/WaterproofingTexture'));
const RoofDampGuard = page(() => import('./pages/products/RoofDampGuard'));
const TileAdhesive = page(() => import('./pages/products/TileAdhesive'));
const TankSeal = page(() => import('./pages/products/TankSeal'));
const Procrete = page(() => import('./pages/products/Procrete'));
const ReadyPlast = page(() => import('./pages/products/ReadyPlast'));
const StoneClear = page(() => import('./pages/products/StoneClear'));
const BDSealOtherSurface = page(() => import('./pages/products/BDSealOtherSurface'));

/* ---------------- Admin ---------------- */
const AdminLogin = page(() => import('./pages/admin/Login'));
const AdminDashboard = page(() => import('./pages/admin/Dashboard'));
const AdminProductList = page(() => import('./pages/admin/products/ProductList'));
const AdminProductForm = page(() => import('./pages/admin/products/ProductForm'));
const AdminBlogList = page(() => import('./pages/admin/blogs/BlogList'));
const AdminBlogForm = page(() => import('./pages/admin/blogs/BlogForm'));
const AdminGuideList = page(() => import('./pages/admin/guides/GuideList'));
const AdminGuideForm = page(() => import('./pages/admin/guides/GuideForm'));
const AdminColorList = page(() => import('./pages/admin/colors/ColorList'));
const AdminColorForm = page(() => import('./pages/admin/colors/ColorForm'));
const AdminPaletteList = page(() => import('./pages/admin/palettes/PaletteList'));
const AdminPaletteForm = page(() => import('./pages/admin/palettes/PaletteForm'));
const AdminColorSchemeList = page(() => import('./pages/admin/colorSchemes/ColorSchemeList'));
const AdminColorSchemeForm = page(() => import('./pages/admin/colorSchemes/ColorSchemeForm'));
const AdminServiceList = page(() => import('./pages/admin/services/ServiceList'));
const AdminServiceForm = page(() => import('./pages/admin/services/ServiceForm'));
const AdminSiteSettings = page(() => import('./pages/admin/SiteSettings'));
const AdminEnquiries = page(() => import('./pages/admin/Enquiries'));
const AdminTds = page(() => import('./pages/admin/TdsAdmin'));
const AdminBanners = page(() => import('./pages/admin/BannersAdmin'));
const AdminEvents = page(() => import('./pages/admin/EventsAdmin'));
const AdminMarmovella = page(() => import('./pages/admin/MarmovellaAdmin'));
const AdminSettings = page(() => import('./pages/admin/SiteSettingsAdmin'));

/** The 22 exterior colour-scheme pages share one template. */
const SCHEME_ROUTES: [string, string][] = [
  ['/Exterior/PeacefulSerene', 'peaceful-serene'],
  ['/Exterior/TrendyStylish', 'trendy-stylish'],
  ['/Exterior/SoftRelaxing', 'soft-relaxing'],
  ['/Exterior/ExcitingBright', 'exciting-bright'],
  ['/Exterior/CraftedCube', 'crafted-cube'],
  ['/Exterior/RomanHeritage', 'roman-heritage'],
  ['/Exterior/CuttingEdge', 'cutting-edge'],
  ['/Exterior/CountryStyle', 'country-style'],
  ['/Exterior/ElegantSimplicity', 'elegant-simplicity'],
  ['/Exterior/RetroRegal', 'retro-regal'],
  ['/Exterior/EternalTemple', 'eternal-temple'],
  ['/Exterior/ModernGeometry', 'modern-geometry'],
  ['/Exterior/GradualElevations', 'gradual-elevations'],
  ['/Exterior/RetroModern', 'retro-modern'],
  ['/Exterior/BeachHouse', 'beach-house'],
  ['/Exterior/LatticeHouse', 'lattice-house'],
  ['/Exterior/ModernBaroque', 'modern-baroque'],
  ['/Exterior/CountryHouse', 'country-house'],
  ['/Exterior/GroovedDesign', 'grooved-design'],
  ['/Exterior/IconicVilla', 'iconic-villa'],
  ['/Exterior/TwistingMaze', 'twisting-maze'],
  ['/Exterior/ArchedResidence', 'arched-residence'],
];

/* ---------------- Route-change loading bar ---------------- */
const RouteFallback = () => (
  <div className="min-h-[60vh]" aria-busy="true" aria-live="polite">
    <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] bg-brand-line overflow-hidden">
      <div className="h-full w-1/3 bg-gradient-to-r from-brand-blue to-brand-red animate-[vpMarquee_1.2s_linear_infinite]" />
    </div>
    <div className="container py-16 space-y-6">
      <div className="vp-skeleton h-10 w-2/3 max-w-md" />
      <div className="vp-skeleton h-5 w-1/2 max-w-sm" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 pt-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="vp-skeleton h-56" />
        ))}
      </div>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <AppShell>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/visualizer" element={<ColorVisualizerPage />} />
          <Route path="/color-guide" element={<ColorGuide />} />
          <Route path="/sample-request" element={<SampleRequest />} />

          {/* Products */}
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/interior" element={<Navigate to="/InteriorWallPaint" replace />} />
          <Route path="/products/exterior" element={<Navigate to="/ExteriorWallPaint" replace />} />
          <Route path="/products/detail/:id" element={<ProductDetail />} />
          <Route path="/products/:category" element={<ProductsPage />} />
          <Route path="/CalculateNow" element={<CalculateNow />} />

          {/* Company */}
          <Route path="/AboutUs/OurStory" element={<OurStory />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />

          {/* Category pages */}
          <Route path="/InteriorWallPaint" element={<InteriorPaintsPage />} />
          <Route path="/InteriorWallPaint/Shades" element={<InteriorColorOptions />} />
          <Route path="/ExteriorWallPaint" element={<ExteriorPaintsPage />} />
          <Route path="/UnderCoat" element={<UnderCoat />} />
          <Route path="/MetalPaints" element={<MetalPaints />} />
          <Route path="/WoodPaints" element={<WoodPaints />} />
          <Route path="/WaterProofingAndConstructionChemicals" element={<WaterProfingAndConstructionChemicals />} />
          <Route path="/OtherSurface" element={<OtherSurface />} />
          <Route path="/textures" element={<TexturesPage />} />
          <Route path="/Marmovella" element={<MarmovellaPage />} />
          <Route path="/textures/marmovella" element={<Navigate to="/Marmovella" replace />} />
          <Route path="/textures/:id" element={<TextureDetail />} />

          {/* Interior products */}
          <Route path="/InteriorWallPaint/FineSquareInterriorEmulsion" element={<FineSquareInteriorEmulsion />} />
          <Route path="/InteriorWallPaint/SilkShine_PearlGloss" element={<SS_PearlGloss />} />
          <Route path="/InteriorWallPaint/CeilingWhite" element={<CeilingWhite />} />
          <Route path="/InteriorWallPaint/EasyWashLuxuryEmulsion" element={<EasyWashLuxuryEmulsion />} />
          <Route path="/InteriorWallPaint/SilkShineSpecialEffectpaints" element={<SilkShineSpecialEffectpaints />} />

          {/* Exterior products */}
          <Route path="/ExteriorWallPaint/FineSquareExteriorEmulsion" element={<FineSquareExteriorEmulsion />} />
          <Route path="/ExteriorWallPaint/PremiumEmlusion" element={<PremiumEmlusion />} />
          <Route path="/ExteriorWallPaint/SelectMattFinish" element={<SelectMattFinish />} />
          <Route path="/ExteriorWallPaint/SelectSheen" element={<SelectSheen />} />
          <Route path="/ExteriorWallPaint/ACMEExteriorEmulsion" element={<ACMEExteriorEmulsion />} />
          <Route path="/ExteriorWallPaint/PrimerSurfaceBrown" element={<PrimerBrown />} />
          <Route path="/ExteriorWallPaint/FineSquareHiSheen" element={<FS_HISHEEN />} />
          <Route path="/ExteriorWallPaint/ACMEWRP" element={<ACMEWrp />} />
          <Route path="/ExteriorWallPaint/SilkShine_SuperGloss" element={<SilkShine_SuperGloss />} />
          <Route path="/ExteriorWallPaint/TitaniumOne" element={<TitaniumOne />} />
          <Route path="/ExteriorWallPaint/UltraWRPExteriorEmulsion" element={<UltraWRPExteriorEmulsion />} />
          <Route path="/Exterior/FineSquare" element={<FineSquare />} />
          <Route path="/Exterior/SilkShine" element={<SilkShine />} />
          <Route path="/Exterior/UltraWrp" element={<UltraWrp />} />
          <Route path="/Exterior/BDSeal" element={<BDSeal />} />
          {/* Legacy range pages now live under the exterior category. */}
          <Route path="/Exterioremulsions" element={<Navigate to="/ExteriorWallPaint" replace />} />
          <Route path="/ApexExterioremulsions" element={<Navigate to="/ExteriorWallPaint" replace />} />
          <Route path="/AcemExterioremulsions" element={<Navigate to="/ExteriorWallPaint" replace />} />

          {/* Exterior colour schemes */}
          {SCHEME_ROUTES.map(([path, slug]) => (
            <Route key={path} path={path} element={<ColorSchemeTemplatePage slug={slug} />} />
          ))}

          {/* Undercoat */}
          <Route path="/UnderCoat/AcmeWaterThinnablePrimer" element={<AcmeWaterThinnablePrimer />} />
          <Route path="/UnderCoat/VertexWaterThinnablePrimer" element={<VertexWaterThinnablePrimer />} />
          <Route path="/UnderCoat/BaseCoat" element={<BaseCoat />} />
          <Route path="/UnderCoat/ultraWallSealer3IN1" element={<UltraWallSealer />} />
          <Route path="/UnderCoat/FineSquareInteriorPrimer" element={<FineSquareInteriorPrimer />} />
          <Route path="/UnderCoat/MG_RedOxide" element={<MGRedOxide />} />
          <Route path="/UnderCoat/ZinchromatePrimer" element={<ZinchromatePrimer />} />
          <Route path="/UnderCoat/ZinPhosphatePrimerWhite" element={<ZinPhosphatePrimerWhite />} />
          <Route path="/UnderCoat/1k_PU_PrimerSurfaceBrown" element={<PrimerSurfaceBrown />} />
          <Route path="/UnderCoat/1k_PU_PrimerSurfaceWhite" element={<PrimerSurfaceWhite />} />
          <Route path="/UnderCoat/DampProofWallPutty" element={<DampProofWallPutty />} />
          <Route path="/UnderCoat/PremiumWallPutty" element={<PremiumWallPutty />} />
          <Route path="/UnderCoat/TitaniumWallPutty" element={<TitaniumWallPutty />} />

          {/* Metal */}
          <Route path="/MetalPaints/Aluminum" element={<Aluminum />} />
          <Route path="/MetalPaints/PU_AluminumPaint" element={<PUAluminumPaint />} />
          <Route path="/MetalPaints/PU_MetallicGoldenPaint" element={<PUMetallicGoldenPaint />} />
          <Route path="/MetalPaints/Silver_Ash_Emerland_Green" element={<SilverAshEmerlandGreen />} />
          <Route path="/MetalPaints/MetalClearGloss" element={<MetalClearGloss />} />
          <Route path="/MetalPaints/MetalClearMatt" element={<MetalClearMatt />} />
          <Route path="/MetalPaints/PremiumGlossEnamel" element={<PremiumGlossEnamel />} />
          <Route path="/MetalPaints/1K_PU_HighPerformanceEnamel" element={<PUHighPerformanceEnamel />} />

          {/* Wood */}
          <Route path="/WoodPaints/PremiumGlossEnamel" element={<PremiumGlossEnamelWoodPaints />} />
          <Route path="/WoodPaints/1K_PU_HighPerformanceEnamel" element={<PUHighPerformanceEnamelWoodPaints />} />
          <Route path="/WoodPaints/WoodGuard" element={<WoodGuard />} />
          <Route path="/WoodPaints/TrueWood_1k_PU_Clear" element={<TrueWoodPUClear />} />
          <Route path="/WoodPaints/ClearVarnish" element={<ClearVarnish />} />

          {/* Waterproofing */}
          <Route path="/WaterProofing/VertexCoolDampProof" element={<VertexCoolDampProof />} />
          <Route path="/WaterProofing/SmartPolymer" element={<SmartPolymer />} />
          <Route path="/WaterProofing/WaterproofingTexture" element={<WaterproofingTexture />} />
          <Route path="/WaterProofing/RoofDampGuard" element={<RoofDampGuard />} />
          <Route path="/WaterProofing/TileAdhesive" element={<TileAdhesive />} />
          <Route path="/WaterProofing/TankSeal" element={<TankSeal />} />
          <Route path="/WaterProofing/Procrete" element={<Procrete />} />
          <Route path="/WaterProofing/ReadyPlast" element={<ReadyPlast />} />
          <Route path="/WaterProofing/StoneClear" element={<StoneClear />} />

          {/* Other surfaces */}
          <Route path="/OtherSurface/BDSeal" element={<BDSealOtherSurface />} />

          {/* Colours */}
          <Route path="/colours/catalogue" element={<ColourCatalogue />} />
          <Route path="/colours/rooms" element={<ColourRoomsPage />} />
          <Route path="/colours/combinations" element={<ColourCombinationsPage />} />
          <Route path="/colours/:room" element={<RoomColoursPage />} />

          {/* Services */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/safe-painting" element={<SafePaintingPage />} />
          <Route path="/services/waterproofing" element={<WaterproofingPage />} />
          <Route path="/services/book-painter" element={<BookPainterPage />} />
          <Route path="/services/colour-consultancy" element={<ColourConsultancyPage />} />
          <Route path="/services/dealer-locator" element={<DealerLocatorPage />} />
          <Route path="/services/paint-calculator" element={<PaintCalculatorPage />} />
          <Route path="/DealerLocator" element={<Navigate to="/services/dealer-locator" replace />} />
          <Route path="/ColourConsultancy" element={<Navigate to="/services/colour-consultancy" replace />} />
          <Route path="/SafePainting" element={<Navigate to="/services/safe-painting" replace />} />

          {/* Content */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/guides" element={<GuideList />} />
          <Route path="/guides/:id" element={<GuideDetail />} />
          <Route path="/TDS" element={<TDS />} />
          <Route path="/QR" element={<URLQRGenerator />} />

          {/* Legal & utilities */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/store-locator" element={<StoreLocatorPage />} />
          <Route path="/paint-budget-calculator" element={<PaintCalculatorPage />} />
          <Route path="/track-order" element={<TrackOrderPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cart" element={<CartPage />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProductList />} />
            <Route path="products/new" element={<AdminProductForm />} />
            <Route path="products/edit" element={<AdminProductForm />} />
            <Route path="blogs" element={<AdminBlogList />} />
            <Route path="blogs/new" element={<AdminBlogForm />} />
            <Route path="blogs/:id/edit" element={<AdminBlogForm />} />
            <Route path="guides" element={<AdminGuideList />} />
            <Route path="guides/new" element={<AdminGuideForm />} />
            <Route path="guides/:id/edit" element={<AdminGuideForm />} />
            <Route path="colors" element={<AdminColorList />} />
            <Route path="colors/new" element={<AdminColorForm />} />
            <Route path="colors/:id/edit" element={<AdminColorForm />} />
            <Route path="palettes" element={<AdminPaletteList />} />
            <Route path="palettes/new" element={<AdminPaletteForm />} />
            <Route path="palettes/:id/edit" element={<AdminPaletteForm />} />
            <Route path="color-schemes" element={<AdminColorSchemeList />} />
            <Route path="color-schemes/new" element={<AdminColorSchemeForm />} />
            <Route path="color-schemes/:id/edit" element={<AdminColorSchemeForm />} />
            <Route path="services" element={<AdminServiceList />} />
            <Route path="services/new" element={<AdminServiceForm />} />
            <Route path="services/:id/edit" element={<AdminServiceForm />} />
            <Route path="site" element={<AdminSiteSettings />} />
            <Route path="enquiries" element={<AdminEnquiries />} />
            <Route path="tds" element={<AdminTds />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="marmovella" element={<AdminMarmovella />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<CatalogFallback />} />
        </Routes>
      </AppShell>
    </Router>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  useRevealOnScroll();
  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </>
    );
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <ScrollToTop />
      <main className="flex-grow">
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;
